/**
 * App-wide keyboard host.
 *
 * Mounted once (in `ProtectedRoute`) for every authenticated page. Installs the
 * global keydown listener, registers the always-available global commands, and
 * lazy-mounts the keyboard overlays from `keyboardOverlayState` so an overlay
 * renders nothing until it is opened. This is the single home for global keyboard
 * chrome; per-surface navigation stays in `useKeyboardNavigation`.
 */

import { FC, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { pendingSavesActions } from '@/store/pendingSavesStore';
import { useGlobalKeybindings } from './useGlobalKeybindings';
import { keyboardOverlayActions, keyboardOverlayState } from './keyboardOverlayStore';
import { useRegisterCommands } from './useRegisterCommands';
import { searchTarget } from './searchTarget';
import { CheatSheet } from './CheatSheet';
import { CommandPalette } from './CommandPalette';

export const KeyboardHost: FC = () => {
  useGlobalKeybindings();
  const snap = useSnapshot(keyboardOverlayState);

  const globalCommands = useMemo(() => [
    {
      descriptor: { id: 'cmd.shortcuts', label: 'Keyboard shortcuts', group: 'Help', keywords: 'cheat sheet keys help', bindingId: 'global.cheatSheet' },
      run: () => keyboardOverlayActions.toggleCheatSheet(),
    },
    {
      descriptor: { id: 'cmd.save', label: 'Save now', group: 'General', keywords: 'flush persist write', bindingId: 'global.save' },
      run: () => pendingSavesActions.flushAll(),
    },
    {
      descriptor: { id: 'cmd.search', label: 'Search', group: 'General', keywords: 'find forms records', bindingId: 'global.search' },
      run: () => { searchTarget.focus(); },
    },
  ], []);
  useRegisterCommands(globalCommands);

  return (
    <>
      {snap.cheatSheetOpen && <CheatSheet />}
      {snap.paletteOpen && <CommandPalette />}
    </>
  );
};
